import { endTimeToMinutes, minutesToHhmm } from "@/utils/bookingTime";

/**
 * Pure room-conflict check used by the "Change room" room selector. A booking may be placed in a
 * room unless it clashes with an existing booking there.
 *
 * Ownership matters (mirrors the server `checkRoomBookingConflict`):
 *  - SAME member: bookings may sit adjacent and share buffers (R6), so only a **core-time overlap**
 *    is a conflict. This is what lets a member move a booking next to their OWN extension without
 *    being blocked by a phantom buffer clash.
 *  - DIFFERENT member: the 15-minute buffer applies — adjacent bookings clash.
 */
export interface RoomConflictBooking {
  id: string;
  memberId: string;
  startTime: string;
  endTime: string;
  hasBuffers?: boolean;
}

const BUFFER_MINUTES = 15;

/** Shift an HHmm time by a number of minutes, clamped to the day. Returns HHmm as a number. */
const shiftHHmm = (hhmm: number, deltaMinutes: number): number => {
  const total = Math.min(24 * 60 - 1, Math.max(0, Math.floor(hhmm / 100) * 60 + (hhmm % 100) + deltaMinutes));
  return Math.floor(total / 60) * 100 + (total % 60);
};

export function hasRoomConflict(
  proposed: { id: string; memberId: string; startTime: string; endTime: string },
  bookings: RoomConflictBooking[],
  // The proposed booking's organisation party (members + owner). When supplied, the org's own
  // bookings may sit adjacent (buffer waived), exactly as the same member's. Omit for non-org
  // bookings — behaviour is then unchanged.
  sameOrgMemberIds?: ReadonlySet<string>,
): boolean {
  const startNum = parseInt(proposed.startTime.padStart(4, "0"), 10);
  // An end at/before the start runs to midnight (utils/bookingTime) — 2400 in HHmm-number terms.
  const toEndNum = (start: string, end: string): number => Number(minutesToHhmm(endTimeToMinutes(end, start)));
  const endNum = toEndNum(proposed.startTime, proposed.endTime);

  return bookings.some((b) => {
    // Never compare with the booking being moved.
    if (b.id === proposed.id) return false;
    // The same occurrence can appear under a different id (e.g. a recurrence instance of the one
    // being moved) — same member at the exact same time is not a real conflict.
    if (
      b.memberId === proposed.memberId &&
      b.startTime.padStart(4, "0") === proposed.startTime.padStart(4, "0") &&
      b.endTime.padStart(4, "0") === proposed.endTime.padStart(4, "0")
    ) {
      return false;
    }

    const sameMember = b.memberId === proposed.memberId;
    const sameOrg = sameOrgMemberIds?.has(b.memberId) ?? false;
    let bStart = parseInt(b.startTime.padStart(4, "0"), 10);
    let bEnd = toEndNum(b.startTime, b.endTime);

    // Apply the 15-minute buffer ONLY for a different party; same-member OR same-org may be adjacent.
    if (b.hasBuffers && !sameMember && !sameOrg) {
      bStart = shiftHHmm(bStart, -BUFFER_MINUTES);
      bEnd = shiftHHmm(bEnd, BUFFER_MINUTES);
    }

    return startNum < bEnd && endNum > bStart;
  });
}
